import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import style from "./CastCrewMemberModal.module.css";
import {
  UploadImage,
  castCrewMemberCreate,
  castCrewMemberUpdate,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

const initialForm = {
  name: "",
  original_name: "",
  image_url: "",
  bio: "",
  dob: "",
  gender: "",
  imdb_id: "",
  tmdb_id: "",
};

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const CastCrewMemberModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData,
  title,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setFormData({
        name: editData.name || "",
        original_name: editData.original_name || "",
        image_url: editData.image_url || "",
        bio: editData.bio || "",
        dob: toDateInputValue(editData.dob),
        gender: editData.gender || "",
        imdb_id: editData.imdb_id || "",
        tmdb_id: editData.tmdb_id ?? "",
      });
      setImagePreview(editData.image_url || "");
    } else {
      setFormData(initialForm);
      setImagePreview("");
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    try {
      setUploadingImage(true);
      const fd = new FormData();
      fd.append("image", file);
      const response = await UploadImage(fd);
      const url =
        response?.data?.data?.upload?.file_url ||
        response?.data?.data?.url ||
        response?.data?.data?.image_url ||
        response?.data?.url ||
        response?.data?.image_url ||
        "";
      if (url) {
        setFormData((prev) => ({ ...prev, image_url: url }));
      } else {
        showToast("Image upload succeeded but no URL returned", "error");
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Image upload failed",
        "error"
      );
      setImagePreview(formData.image_url || "");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const buildPayload = () => {
    const payload = { ...formData };
    if (payload.tmdb_id === "" || payload.tmdb_id === null) {
      delete payload.tmdb_id;
    } else {
      const num = Number(payload.tmdb_id);
      payload.tmdb_id = Number.isNaN(num) ? payload.tmdb_id : num;
    }
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null) delete payload[key];
    });
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast("Name is required", "error");
      return;
    }
    const payload = buildPayload();

    try {
      setSubmitting(true);
      const response = editData?.id
        ? await castCrewMemberUpdate(editData.id, payload)
        : await castCrewMemberCreate(payload);
      showToast(
        response?.data?.message ||
          (editData?.id ? "Updated successfully" : "Created successfully"),
        "success"
      );
      onSuccess?.(response?.data?.data);
      onClose();
    } catch (error) {
      const data = error?.response?.data;
      const fieldErrors = data?.errors
        ? Object.values(data.errors).flat()
        : null;
      showToast(
        fieldErrors?.[0] || data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={style.overlay} onClick={handleOverlayClick}>
      <div className={style.modal}>
        <div className={style.headerRow}>
          <div className={style.heading}>
            {title || (editData?.id ? "Edit Details" : "Add Details")}
          </div>
          <button
            type="button"
            className={style.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={style.row}>
            <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>Name <span style={{ color: "red" }}>*</span></label>
              <input
                className={style.input}
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John"
              />
            </div>
            {/* <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>Original Name</label>
              <input
                className={style.input}
                name="original_name"
                type="text"
                value={formData.original_name}
                onChange={handleChange}
                placeholder="John A"
              />
            </div> */}
          </div>

          <div className={style.row}>
            <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>DOB</label>
              <input
                className={style.input}
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>Gender <span style={{ color: "red" }}>*</span></label>
              <select
                className={style.select}
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={style.field}>
            <label className={style.label}>Bio</label>
            <textarea
              className={style.textarea}
              name="bio"
              value={formData.bio}
              maxLength={750}
              onChange={handleChange}
              placeholder="Bio..."
              rows={3}
            />
          </div>

          <div className={style.row}>
            <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>IMDb ID</label>
              <input
                className={style.input}
                name="imdb_id"
                type="text"
                value={formData.imdb_id}
                onChange={handleChange}
                placeholder="nm1234567"
              />
            </div>
            <div className={style.field} style={{ marginBottom: 0 }}>
              <label className={style.label}>TMDb ID</label>
              <input
                className={style.input}
                name="tmdb_id"
                type="number"
                value={formData.tmdb_id}
                onChange={handleChange}
                placeholder="999"
              />
            </div>
          </div>

          <div className={style.field}>
            <label className={style.label}>Profile Image</label>
            <div
              className={style.uploadBox}
              onClick={handleUploadClick}
              role="button"
              tabIndex={0}
            >
              {imagePreview ? (
                <div className={style.previewWrapper}>
                  <img
                    src={imagePreview}
                    alt="Profile preview"
                    className={style.preview}
                  />
                  {uploadingImage ? (
                    <div className={style.uploading}>Uploading…</div>
                  ) : (
                    <button
                      type="button"
                      className={style.removeImage}
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className={style.uploadTitle}>Profile Image</div>
                  <div className={style.uploadHint}>
                    Portrait &nbsp;·&nbsp; 2:3 &nbsp;·&nbsp; JPG/PNG
                  </div>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          </div>

          <div className={style.actions}>
            <button
              type="button"
              className={style.cancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={style.saveBtn}
              disabled={
                submitting || uploadingImage || !formData.name.trim()
              }
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CastCrewMemberModal;